import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  Loader,
  getTranslatedText,
  CurrencyText,
  COMMON_CONSTANTS,
  useCheckFeatureGating
} from '@nile/components';
import {
  AccountClient,
  GET_BLOCKITEM_CONSUMPTION_INFO,
  GET_BLOCKITEM_INFO,
  MockTicketClient
} from '@nile/graphql';
import { isEmpty } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

const BlockItemConsumptionInfoContainer = styled.div`
  // gap: 12px;
  color: #0b0114;
  opacity: 0.6;
  & > div {
    // flex-grow: 1;
    // flex-basis: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  & > div:not(:first-child) {
    position: relative;
    margin-left: 8px;
    padding-left: 11px;

    &::before {
      left: 0;
      top: 50%;
      width: 3px;
      content: '';
      height: 3px;
      border-radius: 50%;
      position: absolute;
      background: #000000;
      transform: translateY(-50%);
    }
  }
`;

const constantMap = {
  remainingBlockHours:
    'ticket.details.worklog.serviceItem.blockHours.remaining',
  blockHoursUnderAudit:
    'ticket.details.worklog.serviceItem.blockHours.waitingForAudit',
  remainingBlockMoney:
    'ticket.details.worklog.serviceItem.blockMoney.remaining',
  blockMoneyUnderAudit:
    'ticket.details.worklog.serviceItem.blockMoney.waitingForAudit'
};

function getContractId(workItem) {
  if (!workItem) return null;
  if (workItem.contractTicketMapping || workItem.module === 'TICKET') {
    if (workItem.module === 'TICKET' && workItem.ticket) {
      workItem = workItem.ticket;
    }
    return workItem?.contractTicketMapping?.find((ticketMap) => ticketMap.inUse)
      ?.contract?.contractId;
  }

  if (workItem.contract || workItem.module === 'PROJECT') {
    if (workItem.module === 'PROJECT' && workItem.project) {
      workItem = workItem.project;
    }
    return workItem?.contract?.contractId;
  }

  return null;
}

export const BlockItemConsumptionInfo = ({
  selectedItem,
  ticket,
  project,
  endDateTime,
  isAfterHours = false
}) => {
  const [blockItemConsumptionInfo, setBlockItemConsumptionInfo] = useState({});
  const [
    blockItemConsumptionInfoLoading,
    setBlockItemConsumptionInfoLoading
  ] = useState(false);

  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const clientId = ticket
    ? ticket?.client?.accountId
    : project
    ? project?.client?.accountId
    : null;

  const contractId = getContractId(ticket);

  const flags = useFlags();

  const [getBlockItemInfo] = useLazyQuery(GET_BLOCKITEM_INFO, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    onCompleted: (res) => {
      setBlockItemConsumptionInfoLoading(false);
      if (res?.getBlockItemInfo && !isEmpty(res?.getBlockItemInfo)) {
        setBlockItemConsumptionInfo(res.getBlockItemInfo);
      }
    }
  });

  const [getBlockItemConsumptionInfo] = useLazyQuery(
    GET_BLOCKITEM_CONSUMPTION_INFO,
    {
      client: AccountClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      onCompleted: (res) => {
        setBlockItemConsumptionInfoLoading(false);
        if (
          res?.getBlockItemConsumptionInfo &&
          !isEmpty(res?.getBlockItemConsumptionInfo)
        ) {
          setBlockItemConsumptionInfo(res.getBlockItemConsumptionInfo);
        }
      }
    }
  );

  useEffect(() => {
    if (!selectedItem?.itemId) return;

    const blockItemConsumptionInput = {
      [contractsNew ? 'serviceCatalogItem' : 'serviceItem']: {
        itemId: selectedItem?.itemId
      },
      site:
        ticket?.site?.id || project?.site?.id
          ? {
              id: ticket?.site?.id || project?.site?.id
            }
          : null
    };

    if (endDateTime) {
      blockItemConsumptionInput.eventEndDateTime = endDateTime;
    }

    if (contractsNew) {
      if (contractId) {
        blockItemConsumptionInput.contract = {
          contractId
        };
        blockItemConsumptionInput.afterHours = isAfterHours;
        setBlockItemConsumptionInfoLoading(true);
        getBlockItemInfo({
          variables: {
            input: blockItemConsumptionInput
          }
        });
      }
    } else if (clientId) {
      blockItemConsumptionInput.client = {
        accountId: clientId
      };
      setBlockItemConsumptionInfoLoading(true);
      getBlockItemConsumptionInfo({
        variables: {
          input: blockItemConsumptionInput
        }
      });
    }
  }, [
    selectedItem,
    endDateTime,
    isAfterHours,
    contractId,
    clientId,
    contractsNew
  ]);

  const BlockItemInfo = ({ type }) => {
    const keys = Object.keys(blockItemConsumptionInfo).filter((blockItem) =>
      blockItem.toLowerCase().includes(type)
    );
    const values = [];
    keys.forEach((val) => {
      if (blockItemConsumptionInfo?.[val]) {
        values.push(
          <BlockItem
            currency={type === 'money'}
            value={blockItemConsumptionInfo?.[val]}
            constant={constantMap[val]}
          />
        );
      }
    });
    return keys.length && values.length ? (
      <BlockItemConsumptionInfoContainer className="flex center ml-40">
        {values}
      </BlockItemConsumptionInfoContainer>
    ) : null;
  };

  return blockItemConsumptionInfoLoading ? (
    <Loader />
  ) : (
    !isEmpty(blockItemConsumptionInfo) && (
      <>
        <BlockItemInfo type="hours" />
        <BlockItemInfo type="money" />
      </>
    )
  );
};

const BlockItem = ({ currency, value, constant }) => {
  const intl = useIntl();
  return (
    <div title={`${value} ${getTranslatedText(intl, constant)}`}>
      {currency ? <CurrencyText value={value} /> : value}{' '}
      <FormattedMessage id={constant} />
    </div>
  );
};
